import React, { useState, useRef, useEffect, useCallback } from "react";

const GenericSelectTailwind = ({
  value,
  onChange,
  options = [],
  placeholder = "Auswählen...",
  disabled = false,
  leftIcon = null,
  iconSize = "w-5 h-5",
  openUpward = false,
  whiteBackground = false,
  greyBackground = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionRefs = useRef([]);

  const selectedOption = options.find(option => option.value === value);
  const hasSearch = options.length > 5;

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Schließe Dropdown beim Klicken außerhalb
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
        setFocusedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fokussierte Option ins Sichtfeld scrollen
  useEffect(() => {
    if (focusedIndex >= 0 && optionRefs.current[focusedIndex]) {
      optionRefs.current[focusedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [focusedIndex]);

  // focusedIndex beim Filtern zurücksetzen
  useEffect(() => {
    if (isOpen) {
      const selIdx = filteredOptions.findIndex(o => o.value === value);
      setFocusedIndex(selIdx >= 0 ? selIdx : filteredOptions.length > 0 ? 0 : -1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const openDropdown = useCallback(() => {
    if (disabled) return;
    setSearchTerm("");
    const selIdx = options.findIndex(o => o.value === value);
    setFocusedIndex(selIdx >= 0 ? selIdx : 0);
    setIsOpen(true);
    if (hasSearch) {
      setTimeout(() => searchInputRef.current?.focus(), 10);
    }
  }, [disabled, options, value, hasSearch]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchTerm("");
    setFocusedIndex(-1);
  }, []);

  const handleOptionSelect = (option) => {
    onChange(option.value);
    closeDropdown();
  };

  const handleToggle = () => {
    if (isOpen) closeDropdown();
    else openDropdown();
  };

  // Tastatursteuerung am Trigger-Button
  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!isOpen) {
        openDropdown();
      } else if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
        handleOptionSelect(filteredOptions[focusedIndex]);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) openDropdown();
      else setFocusedIndex(i => Math.min(i + 1, filteredOptions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!isOpen) openDropdown();
      else setFocusedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeDropdown();
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      if (!isOpen) {
        if (hasSearch) {
          setSearchTerm(e.key);
          setIsOpen(true);
          setTimeout(() => searchInputRef.current?.focus(), 10);
        } else {
          const match = options.find(opt =>
            opt.label.toLowerCase().startsWith(e.key.toLowerCase())
          );
          if (match) onChange(match.value);
        }
      }
    }
  };

  // Tastatursteuerung im Suchfeld
  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex(i => Math.min(i + 1, filteredOptions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
        handleOptionSelect(filteredOptions[focusedIndex]);
      } else if (filteredOptions.length === 1) {
        handleOptionSelect(filteredOptions[0]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeDropdown();
    } else if (e.key === "Tab") {
      closeDropdown();
    }
  };

  // Trigger-Button Farben
  const triggerColor = disabled
    ? whiteBackground
      ? "bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500 cursor-not-allowed border-slate-200 dark:border-slate-700"
      : greyBackground
        ? "bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed border-slate-200 dark:border-slate-600"
        : "bg-slate-50 dark:bg-slate-700/50 text-slate-400 dark:text-slate-500 cursor-not-allowed border-slate-200 dark:border-slate-600"
    : whiteBackground
      ? "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500"
      : greyBackground
        ? "bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500"
        : "bg-slate-50 dark:bg-slate-700/50 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500";

  const hasIcon = !!(selectedOption?.icon || leftIcon);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger */}
      <button
        type="button"
        onClick={handleToggle}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        className={`
          w-full h-10 px-3 text-left flex items-center gap-2
          border rounded-xl transition-colors
          focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20
          focus:border-primary dark:focus:border-primary-light
          ${triggerColor}
        `}
      >
        {hasIcon && (
          <span className={`${iconSize} flex items-center justify-center flex-shrink-0`}>
            {selectedOption?.icon || leftIcon}
          </span>
        )}
        <span className={`flex-1 text-sm truncate ${selectedOption ? "font-medium" : "text-slate-400 dark:text-slate-500"}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <i className={`fas fa-chevron-down flex-shrink-0 text-slate-400 dark:text-slate-500 text-[10px] transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown-Panel */}
      {isOpen && (
        <div
          className={`
            absolute z-[99999] w-full min-w-[10rem]
            ${openUpward ? "mb-1.5 bottom-full" : "mt-1.5 top-full"}
            bg-white dark:bg-slate-800
            border border-slate-200 dark:border-slate-700
            rounded-xl shadow-lg
            overflow-hidden
          `}
        >
          {/* Suchfeld (ab 6 Optionen) */}
          {hasSearch && (
            <div className="p-2 border-b border-slate-100 dark:border-slate-700/60">
              <div className="relative">
                <i className="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-[10px] pointer-events-none" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className="w-full h-8 pl-7 pr-3 text-xs border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  autoFocus
                />
              </div>
            </div>
          )}

          {/* Optionsliste */}
          <div className="max-h-52 overflow-y-auto py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, idx) => {
                const isSelected = value === option.value;
                const isFocused = focusedIndex === idx;
                return (
                  <button
                    key={option.value}
                    ref={el => { optionRefs.current[idx] = el; }}
                    type="button"
                    onClick={() => handleOptionSelect(option)}
                    onMouseEnter={() => setFocusedIndex(idx)}
                    className={`
                      w-full px-3 py-2 text-left flex items-center gap-2.5
                      focus:outline-none transition-colors
                      ${isSelected
                        ? "bg-primary/8 dark:bg-primary-light/10"
                        : isFocused
                          ? "bg-slate-100 dark:bg-slate-700"
                          : "hover:bg-slate-50 dark:hover:bg-slate-700/50"
                      }
                    `}
                  >
                    {option.icon && (
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        {option.icon}
                      </span>
                    )}
                    <span className={`flex-1 text-sm truncate ${isSelected ? "font-semibold text-primary dark:text-primary-light" : "font-medium text-slate-700 dark:text-slate-300"}`}>
                      {option.label}
                    </span>
                    {isSelected && (
                      <i className="fas fa-check text-primary dark:text-primary-light text-[10px] flex-shrink-0" />
                    )}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-3 text-xs text-slate-400 dark:text-slate-500 text-center">
                Keine Optionen gefunden
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericSelectTailwind;
